import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { isAxiosError } from 'axios';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { client } from '../services/api';
import {
  getCurrentFormSchemaVersionsRogerdocFormSchemasCurrentGet,
  getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet,
  getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost,
  listBespokeTemplatesBespokeTemplatesGet,
  createBespokeTemplateBespokeTemplatesPost,
  updateBespokeTemplateBespokeTemplatesTemplateIdPatch,
  deleteBespokeTemplateBespokeTemplatesTemplateIdDelete,
} from '../services/api/sdk.gen';
import type {
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
  VersionedQuestionSchema,
  BespokeTemplateResponse,
  BespokeTemplateCreate,
  BespokeTemplateUpdate,
} from '../services/api/types.gen';
import type { VisitTypeId } from '../services/api/models/VisitTypeId';
import type { RogerDocFormType } from '../services/api/models/RogerDocFormType';
import { useToast } from '../hooks/useToastContext';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { TemplateVibecoder } from './formSchema/TemplateVibecoder';
import { formatSectionDropdownLabel } from './formSchema/formatSectionDropdownLabel';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  Pencil,
  ShieldX,
  Trash2,
} from 'lucide-react';

function bespokeApiErrorMessage(err: unknown): string {
  if (!isAxiosError(err)) {
    return 'Something went wrong. Please try again.';
  }
  const data = err.response?.data as { detail?: unknown } | undefined;
  const detail = data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === 'object' && first && 'msg' in first) {
      return String((first as { msg: string }).msg);
    }
  }
  return err.message || 'Something went wrong. Please try again.';
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function isEffective(v: FormSchemaVersion): boolean {
  const now = new Date();
  const from = v.effective_from ? new Date(v.effective_from) : null;
  const until = v.effective_until ? new Date(v.effective_until) : null;
  if (from && now < from) return false;
  if (until && now > until) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Section: Form Tree Preview
// ---------------------------------------------------------------------------

interface FormTreeQuestion {
  qid: string;
  question: string;
  type?: string | null;
  subitems?: Record<string, FormTreeQuestion[]>;
}

type FormTree = Record<string, FormTreeQuestion[]>;

function FormTreeSection({
  name,
  questions,
  isBespoke,
}: {
  name: string;
  questions: FormTreeQuestion[];
  isBespoke: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className='border rounded-md'>
      <button
        onClick={() => setOpen(o => !o)}
        className='flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-muted/50 transition-colors'
      >
        {open ? (
          <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground' />
        ) : (
          <ChevronRight className='h-4 w-4 shrink-0 text-muted-foreground' />
        )}
        <span className='font-medium text-sm flex-1'>{name}</span>
        <span className='text-xs text-muted-foreground'>
          {questions.length} question{questions.length !== 1 ? 's' : ''}
        </span>
        {isBespoke && (
          <Badge variant='secondary' className='text-xs'>
            Bespoke
          </Badge>
        )}
      </button>
      {open && (
        <div className='border-t px-4 py-2 space-y-1'>
          {questions.map(q => (
            <div key={q.qid} className='flex items-center gap-3 py-1 text-sm'>
              <code className='text-xs text-muted-foreground font-mono w-40 shrink-0 truncate'>
                {q.qid}
              </code>
              <span className='flex-1 truncate'>{q.question}</span>
              {q.type && (
                <Badge variant='outline' className='text-xs shrink-0'>
                  {q.type}
                </Badge>
              )}
              {q.subitems && Object.keys(q.subitems).length > 0 && (
                <Badge variant='outline' className='text-xs shrink-0'>
                  subitems
                </Badge>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Main Page
// ---------------------------------------------------------------------------

const FormSchemas: React.FC = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
    }))
  );
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [formTypeCatalog, setFormTypeCatalog] = useState<
    RogerDocFormTypeCatalogEntry[]
  >([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // -- state: schema versions browser --
  const [selectedRogerDocFormType, setSelectedRogerDocFormType] =
    useState<RogerDocFormType | null>(null);
  const [versions, setVersions] = useState<FormSchemaVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // -- state: form tree preview --
  const [selectedVersion, setSelectedVersion] =
    useState<FormSchemaVersion | null>(null);
  const [formTree, setFormTree] = useState<FormTree | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);

  // -- state: bespoke templates --
  const [templates, setTemplates] = useState<BespokeTemplateResponse[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const selectedCatalogEntry = useMemo(
    () =>
      formTypeCatalog.find(r => r.form_type === selectedRogerDocFormType) ??
      null,
    [formTypeCatalog, selectedRogerDocFormType]
  );

  const canonicalVisitForApi: VisitTypeId | null =
    selectedCatalogEntry?.canonical_visit_type_id ?? null;

  const [vibecoderJsonEdit, setVibecoderJsonEdit] =
    useState<BespokeTemplateResponse | null>(null);

  const [saving, setSaving] = useState(false);

  // -- fetch schema versions when visit type changes --
  const fetchVersions = useCallback(async (visitTypeApi: VisitTypeId) => {
    setVersionsLoading(true);
    setSelectedVersion(null);
    setFormTree(null);
    try {
      const res =
        await getCurrentFormSchemaVersionsRogerdocFormSchemasCurrentGet({
          query: { visit_type: visitTypeApi },
          client,
        });
      setVersions((res?.data as FormSchemaVersion[] | undefined) ?? []);
    } catch (err) {
      console.error('Failed to fetch schema versions:', err);
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setCatalogLoading(true);
      try {
        const res = await getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet({
          client,
        });
        const rows =
          (res?.data as RogerDocFormTypeCatalogEntry[] | undefined) ?? [];
        if (cancelled) return;
        setFormTypeCatalog(rows);
        setSelectedRogerDocFormType(prev => {
          if (prev != null && rows.some(r => r.form_type === prev)) {
            return prev;
          }
          return rows[0]?.form_type ?? null;
        });
      } catch (err) {
        console.error('Failed to load Roger Doc form type catalog:', err);
        if (!cancelled) {
          setFormTypeCatalog([]);
          setSelectedRogerDocFormType(null);
          toastRef.current.error(
            'Catalog unavailable',
            'Could not load Roger Doc form types. Refresh to retry.'
          );
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (canonicalVisitForApi == null) return;
    void fetchVersions(canonicalVisitForApi);
  }, [canonicalVisitForApi, fetchVersions]);

  // -- fetch the form tree for a selected version --
  // The build endpoint returns {version, schema_data} for a specific
  // (name, version) selector; we use schema_data as the form tree.
  const fetchFormTree = useCallback(
    async (version: FormSchemaVersion) => {
      if (canonicalVisitForApi == null) return;
      setTreeLoading(true);
      try {
        const res =
          await getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost({
            query: { visit_type: canonicalVisitForApi },
            body: {
              schema_version: {
                name: version.name,
                version: version.version ?? null,
              },
            },
            client,
          });
        const payload = res?.data as VersionedQuestionSchema | undefined;
        setFormTree(payload ? (payload.schema_data as FormTree) : null);
      } catch (err) {
        console.error('Failed to fetch form tree:', err);
        setFormTree(null);
      } finally {
        setTreeLoading(false);
      }
    },
    [canonicalVisitForApi]
  );

  const handleVersionClick = (v: FormSchemaVersion) => {
    setSelectedVersion(v);
    void fetchFormTree(v);
  };

  // -- fetch bespoke templates --
  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await listBespokeTemplatesBespokeTemplatesGet({
        client,
        throwOnError: true,
      });
      setTemplates((res?.data as BespokeTemplateResponse[] | undefined) ?? []);
    } catch (err) {
      console.error('Failed to fetch bespoke templates:', err);
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  // -- bespoke CRUD handlers --
  const handlePersistVibecoderJson = async (
    payload: BespokeTemplateCreate | BespokeTemplateUpdate
  ) => {
    setSaving(true);
    try {
      if (vibecoderJsonEdit) {
        await updateBespokeTemplateBespokeTemplatesTemplateIdPatch({
          path: { template_id: vibecoderJsonEdit.id },
          body: payload as BespokeTemplateUpdate,
          client,
          throwOnError: true,
        });
        toast.success('Template Updated', 'JSON layout saved.');
        setVibecoderJsonEdit(null);
      } else {
        await createBespokeTemplateBespokeTemplatesPost({
          body: payload as BespokeTemplateCreate,
          client,
          throwOnError: true,
        });
        toast.success('Template Created', 'JSON bespoke template registered.');
      }
      void fetchTemplates();
    } catch (err) {
      console.error('Failed to persist JSON bespoke template:', err);
      toast.error('Save Failed', bespokeApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteBespokeTemplateBespokeTemplatesTemplateIdDelete({
        path: { template_id: id },
        client,
        throwOnError: true,
      });
      toast.success('Template Deleted', 'Bespoke template removed.');
      void fetchTemplates();
    } catch (err) {
      console.error('Failed to delete bespoke template:', err);
      toast.error('Delete Failed', bespokeApiErrorMessage(err));
    }
  };

  const handleToggleEnabled = async (t: BespokeTemplateResponse) => {
    try {
      await updateBespokeTemplateBespokeTemplatesTemplateIdPatch({
        path: { template_id: t.id },
        body: { is_enabled: !t.is_enabled },
        client,
        throwOnError: true,
      });
      void fetchTemplates();
    } catch (err) {
      console.error('Failed to toggle bespoke template:', err);
      toast.error('Update Failed', bespokeApiErrorMessage(err));
    }
  };

  const bespokeSet = new Set(
    templates
      .filter(t => t.is_enabled)
      .map(t => `${t.schema_version_name}::${t.section_name}`)
  );

  // -- permission guard --
  if (!hasPermission('emr:manage')) {
    return (
      <Card className='max-w-md mx-auto mt-8'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-destructive'>
            <ShieldX className='h-5 w-5' />
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-muted-foreground'>
            You don&apos;t have permission to access Form Schema management.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h1 className='text-3xl font-bold'>Form Schema Manager</h1>
        <p className='text-muted-foreground'>
          Browse form schema versions, preview question trees, and manage
          bespoke template registrations.
        </p>
      </div>

      {/* Section 1: Schema Version Browser */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <FileText className='h-5 w-5' />
            Schema Versions
          </CardTitle>
          <CardDescription>
            Form types and labels are loaded from the RogerDoc form-type catalog
            API (Python source:{' '}
            <code className='text-xs'>
              app.models.enum.visit.RogerDocFormType
            </code>
            ).
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='max-w-xs'>
            <Select
              value={selectedRogerDocFormType ?? ''}
              onValueChange={v =>
                setSelectedRogerDocFormType(v as RogerDocFormType)
              }
              disabled={catalogLoading || formTypeCatalog.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    catalogLoading ? 'Loading form types…' : 'No form types'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {formTypeCatalog.map(row => (
                  <SelectItem key={row.form_type} value={row.form_type}>
                    {row.display_label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Form Type</TableHead>
                  <TableHead>Effective From</TableHead>
                  <TableHead>Effective Until</TableHead>
                  <TableHead className='text-right'>Sections</TableHead>
                  <TableHead className='text-right'>Questions</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {versionsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className='text-center py-8'>
                      <Loader2 className='h-5 w-5 animate-spin mx-auto' />
                    </TableCell>
                  </TableRow>
                ) : versions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className='text-center py-8 text-muted-foreground'
                    >
                      No schema versions found for this visit type.
                    </TableCell>
                  </TableRow>
                ) : (
                  versions.map(v => {
                    const active = isEffective(v);
                    const versionKey = `${v.name}::${v.version ?? ''}`;
                    const selectedKey = selectedVersion
                      ? `${selectedVersion.name}::${selectedVersion.version ?? ''}`
                      : null;
                    const selected = selectedKey === versionKey;
                    return (
                      <TableRow
                        key={versionKey}
                        className={`cursor-pointer ${selected ? 'bg-muted' : 'hover:bg-muted/50'}`}
                        onClick={() => handleVersionClick(v)}
                      >
                        <TableCell className='font-medium'>
                          {v.name}
                          {active && (
                            <Badge className='ml-2 text-xs' variant='default'>
                              Active
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{v.form_type}</TableCell>
                        <TableCell>{formatDate(v.effective_from)}</TableCell>
                        <TableCell>{formatDate(v.effective_until)}</TableCell>
                        <TableCell className='text-right'>
                          {v.section_count ?? '—'}
                        </TableCell>
                        <TableCell className='text-right'>
                          {v.question_count ?? '—'}
                        </TableCell>
                        <TableCell>
                          <ChevronRight className='h-4 w-4 text-muted-foreground' />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Form Tree Preview */}
      {selectedVersion && (
        <Card>
          <CardHeader>
            <CardTitle>Form Tree: {selectedVersion.name}</CardTitle>
            <CardDescription>
              Expand a section to see its questions. Sections with bespoke
              templates are labelled.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {treeLoading ? (
              <div className='flex items-center justify-center py-8'>
                <Loader2 className='h-5 w-5 animate-spin' />
              </div>
            ) : formTree ? (
              <div className='space-y-2'>
                {Object.entries(formTree).map(([section, questions]) => (
                  <FormTreeSection
                    key={section}
                    name={formatSectionDropdownLabel(section)}
                    questions={questions}
                    isBespoke={bespokeSet.has(
                      `${selectedVersion.name}::${section}`
                    )}
                  />
                ))}
              </div>
            ) : (
              <p className='text-muted-foreground text-center py-8'>
                Failed to load form tree.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bespoke Template Registry */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Bespoke Template Registry</CardTitle>
            <CardDescription>
              Manage which schema version + section combinations use JSON
              bespoke layouts (Template Vibecoder).
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Schema Version</TableHead>
                  <TableHead>Section</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className='text-center'>Enabled</TableHead>
                  <TableHead className='text-right'>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templatesLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className='text-center py-8'>
                      <Loader2 className='h-5 w-5 animate-spin mx-auto' />
                    </TableCell>
                  </TableRow>
                ) : templates.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className='text-center py-8 text-muted-foreground'
                    >
                      No bespoke templates registered yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map(t => (
                    <TableRow key={t.id}>
                      <TableCell className='font-medium'>
                        {t.schema_version_name}
                      </TableCell>
                      <TableCell>
                        {formatSectionDropdownLabel(t.section_name)}
                      </TableCell>
                      <TableCell>{t.name}</TableCell>
                      <TableCell className='text-muted-foreground max-w-[200px] truncate'>
                        {t.description || '—'}
                      </TableCell>
                      <TableCell className='text-center'>
                        <Switch
                          checked={t.is_enabled}
                          onCheckedChange={() => void handleToggleEnabled(t)}
                        />
                      </TableCell>
                      <TableCell className='text-right'>
                        <div className='flex items-center justify-end gap-1'>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => setVibecoderJsonEdit(t)}
                          >
                            <Pencil className='h-4 w-4' />
                          </Button>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => void handleDelete(t.id)}
                          >
                            <Trash2 className='h-4 w-4 text-destructive' />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <TemplateVibecoder
        editingTemplate={vibecoderJsonEdit}
        onDiscardEdit={() => setVibecoderJsonEdit(null)}
        onSaveJson={handlePersistVibecoderJson}
        saving={saving}
        formTypeCatalog={formTypeCatalog}
        catalogLoading={catalogLoading}
      />
    </div>
  );
};

export default FormSchemas;
